import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Eye, User } from 'lucide-react';
import { unidadesApi } from '../../../lib/api';
import { PropietarioUnidadDetalle } from '../../propietario/PropietarioUnidadDetalle';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../../components/ui/select';
import { EmptyState } from '../../../components/common/EmptyState';

// "11. Vista Inversor" (solo analista): replica EXACTAMENTE lo que ve el inversor en su
// portal (Resumen · Facturación y Cobro · Inquilino), con datos en tiempo real (incluye
// borradores, sin necesidad de publicar). Selector de unidad + selector de propietario
// para copropiedades. Misma lógica que el preview de Expensas.
export const TabVistaInversor = ({ edificioId }) => {
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unidadId, setUnidadId] = useState('');
  const [propietarioId, setPropietarioId] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await unidadesApi.list({ edificio_id: edificioId });
        const all = res.data || [];
        const fullMap = {};
        all.forEach((u) => { fullMap[u.unidad_id] = u; });
        // "Administrada por GC" efectiva: un componente hereda el valor de su unidad principal.
        const esAdmin = (u) => {
          if (u.rol_unidad === 'componente' && u.unidad_principal_id && fullMap[u.unidad_principal_id]) {
            return fullMap[u.unidad_principal_id].en_administracion === true;
          }
          return u.en_administracion === true;
        };
        const tieneProp = (u) => (u.propietarios?.length > 0) || !!u.propietario_id;
        const visibles = all
          .filter((u) => u.rol_unidad !== 'componente' && esAdmin(u) && tieneProp(u))
          .sort((a, b) => (a.nombre_unidad || '').localeCompare(b.nombre_unidad || '', 'es', { numeric: true }));
        if (!cancelled) {
          setUnidades(visibles);
          if (visibles.length) setUnidadId(visibles[0].unidad_id);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [edificioId]);

  const unidadSel = useMemo(
    () => unidades.find((u) => u.unidad_id === unidadId),
    [unidades, unidadId]
  );

  const propietarios = useMemo(() => {
    if (!unidadSel) return [];
    if (unidadSel.propietarios?.length) {
      return unidadSel.propietarios.map((p) => ({
        id: p.propietario_id,
        nombre: p.propietario_nombre || 'Propietario',
        porcentaje: p.porcentaje,
      }));
    }
    if (unidadSel.propietario_id) {
      return [{ id: unidadSel.propietario_id, nombre: unidadSel.propietario_nombre || 'Propietario', porcentaje: 100 }];
    }
    return [];
  }, [unidadSel]);

  useEffect(() => {
    setPropietarioId(propietarios.length ? propietarios[0].id : '');
  }, [propietarios]);

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!unidades.length) {
    return (
      <div className="py-10">
        <EmptyState
          icon={Eye}
          title="Sin unidades para previsualizar"
          description="No hay unidades administradas con inversor asignado en este edificio."
        />
      </div>
    );
  }

  return (
    <div data-testid="vista-inversor-tab" className="space-y-3">
      {/* Barra de selectores */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 md:px-4 py-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-[#1B4D3E]">
          <Eye className="w-4 h-4" />
          Vista Inversor
        </div>
        <div className="hidden md:block h-5 w-px bg-slate-200" />
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 flex-1">
          <div className="w-full sm:w-64">
            <Select value={unidadId} onValueChange={setUnidadId}>
              <SelectTrigger data-testid="vista-inversor-unidad-select" className="h-9 text-sm">
                <SelectValue placeholder="Elegí una unidad" />
              </SelectTrigger>
              <SelectContent>
                {unidades.map((u) => (
                  <SelectItem key={u.unidad_id} value={u.unidad_id} data-testid={`vista-inversor-unidad-${u.unidad_id}`}>
                    {u.nombre_unidad}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {propietarios.length > 1 && (
            <div className="w-full sm:w-72">
              <Select value={propietarioId} onValueChange={setPropietarioId}>
                <SelectTrigger data-testid="vista-inversor-propietario-select" className="h-9 text-sm">
                  <User className="w-3.5 h-3.5 text-slate-400 mr-1" />
                  <SelectValue placeholder="Elegí un propietario" />
                </SelectTrigger>
                <SelectContent>
                  {propietarios.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`vista-inversor-propietario-${p.id}`}>
                      {p.nombre}{p.porcentaje != null && p.porcentaje < 100 ? ` (${p.porcentaje}%)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="text-[11px] text-slate-400">
          Vista en tiempo real · así lo ve el inversor
        </div>
      </div>

      {/* Portal del inversor embebido (mismo componente que ve el propietario) */}
      {unidadId && propietarioId && (
        <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
          <PropietarioUnidadDetalle
            key={`${unidadId}-${propietarioId}`}
            mode="renta"
            unidadIdProp={unidadId}
            previewPropietarioId={propietarioId}
            previewMode
          />
        </div>
      )}
    </div>
  );
};

export default TabVistaInversor;
